import { MarkdownEditorField } from '@/components/markdown/markdown-editor-field'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
} from '@/components/ui/card'
import { FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/hooks/use-auth'
import { Check, Loader2 } from 'lucide-react'
import { createFileRoute, getRouteApi } from '@tanstack/react-router'
import { CardHeaderWithBadge } from './-components/card-header-with-badge'
import { useOrganizationProfileForm, validateUrl } from './-components/use-organization-profile-form'

const parentRoute = getRouteApi('/_authed/_user/organization')

export const Route = createFileRoute(
  '/_authed/_user/organization/organization-profile',
)({
  component: CompanySection,
  beforeLoad: () => ({
    getTitle: () => 'Profile',
    getDescription: () => 'Review and update your organization profile to help buyers understand your business',
  }),
})

function CompanySection() {
  const { canEditOrgSettings: canEdit } = useAuth()
  const { initialData, hasCompanyInfo } = parentRoute.useLoaderData()

  const { form, NavBlocker } = useOrganizationProfileForm({
    initialData,
  })

  return (
    <>
      <Card data-tour="org-profile-card" className={canEdit ? '' : 'opacity-75'}>

        <CardHeaderWithBadge
          title="Basic Information"
          description="Provide basic information about your company to help buyers understand your business."
          form={form}
          fieldName={['companyName', 'websiteUrl', 'yearFounded', 'naicsCode', 'naicsCodeDescription', 'countryName', 'regionName']}
          hasCompanyInfo={hasCompanyInfo}
        />
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name</Label>
              <form.Field name="companyName">
                {field => (
                  <Input
                    id="companyName"
                    value={field.state.value || ''}
                    placeholder="Your company name"
                    disabled={!canEdit}
                    onChange={event_ =>
                      field.handleChange(event_.target.value)}
                  />
                )}
              </form.Field>
            </div>
            <div className="space-y-2">
              <Label htmlFor="websiteUrl">Website URL</Label>
              <form.Field
                name="websiteUrl"
                validators={{
                  onBlur: ({ value }) => validateUrl(value),
                  onChange: ({ value }) => validateUrl(value),
                }}
              >
                {(field) => {
                  const hasError = field.state.meta.isTouched && field.state.meta.errors.length > 0
                  return (
                    <>
                      <Input
                        id="websiteUrl"
                        value={field.state.value || ''}
                        placeholder="https://example.com"
                        disabled={!canEdit}
                        aria-invalid={hasError}
                        className={hasError ? 'border-destructive' : ''}
                        onChange={event_ => field.handleChange(event_.target.value)}
                        onBlur={field.handleBlur}
                      />
                      {hasError && (
                        <FieldError errors={field.state.meta.errors.map(e => e ? { message: e } : undefined)} />
                      )}
                    </>
                  )
                }}
              </form.Field>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="yearFounded">Year Founded</Label>
              <form.Field name="yearFounded">
                {field => (
                  <Input
                    id="yearFounded"
                    type="number"
                    value={field.state.value || ''}
                    placeholder="2000"
                    disabled={!canEdit}
                    onChange={event_ =>
                      field.handleChange(Number(event_.target.value) || undefined)}
                  />
                )}
              </form.Field>
            </div>
            <div className="space-y-2">
              <Label htmlFor="naicsCode">NAICS Code</Label>
              <form.Field name="naicsCode">
                {field => (
                  <Input
                    id="naicsCode"
                    value={field.state.value || ''}
                    placeholder="e.g. 123456"
                    disabled={!canEdit}
                    onChange={event_ =>
                      field.handleChange(event_.target.value)}
                  />
                )}
              </form.Field>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="naicsCodeDescription">Industry</Label>
            <form.Field name="naicsCodeDescription">
              {field => (
                <Input
                  id="naicsCodeDescription"
                  value={field.state.value || ''}
                  placeholder="e.g. Manufacturing"
                  disabled={!canEdit}
                  onChange={event_ => field.handleChange(event_.target.value)}
                />
              )}
            </form.Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="countryName">Country</Label>
              <form.Field name="countryName">
                {field => (
                  <Input
                    id="countryName"
                    name="countryName"
                    value={field.state.value || ''}
                    placeholder="United States"
                    disabled={!canEdit}
                    onChange={event_ =>
                      field.handleChange(event_.target.value)}
                  />
                )}
              </form.Field>
            </div>
            <div className="space-y-2">
              <Label htmlFor="regionName">Region/State</Label>
              <form.Field name="regionName">
                {field => (
                  <Input
                    id="regionName"
                    name="regionName"
                    value={field.state.value || ''}
                    placeholder="California"
                    disabled={!canEdit}
                    onChange={event_ =>
                      field.handleChange(event_.target.value)}
                  />
                )}
              </form.Field>
            </div>
          </div>

          {canEdit && (
            <form.Subscribe selector={state => ({
              isSubmitting: state.isSubmitting,
              canSubmit: state.canSubmit,
            })}
            >
              {({ isSubmitting, canSubmit }) => (
                <div className="flex justify-end pt-2">
                  <Button
                    type="button"
                    disabled={isSubmitting || !canSubmit}
                    onClick={() => form.handleSubmit()}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="mr-2 h-4 w-4" />
                        Save
                      </>
                    )}
                  </Button>
                </div>
              )}
            </form.Subscribe>
          )}
        </CardContent>
      </Card>

      <Card className={canEdit ? '' : 'opacity-75'}>
        <CardHeaderWithBadge
          title="Company Description"
          description="Provide a clear, concise description of your company and what you offer."
          form={form}
          fieldName="companyDescription"
          hasCompanyInfo={hasCompanyInfo}
        />
        <CardContent>
          <MarkdownEditorField
            form={form}
            name="companyDescription"
            displayModeByDefault
            placeholder="Enter a description of your company here..."
            disabled={!canEdit}
            showSaveButton={canEdit}
            onSave={() => form.handleSubmit()}
          />
        </CardContent>
      </Card>
      <NavBlocker />
    </>
  )
}
